import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';

import Scrollbar from '../../../components/Scrollbar';
import { postNumberSwapBindings } from '../../../services/simSwap';
import { downloadResultsExcel, parseBindingsFile } from './excel';
import { validateBindingRow } from './validation';

const STATUSES = [
  { key: 'In Progress', label: 'In Progress', color: 'info' },
  { key: 'Success', label: 'Success', color: 'success' },
  { key: 'Failed', label: 'Failed', color: 'error' },
];

const STATUS_BY_KEY = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: s }), {});

const StatusChip = ({ status }) =>
  status ? (
    <Chip label={status} color={STATUS_BY_KEY[status]?.color || 'default'} size="small" />
  ) : null;

const COLUMNS = [
  { id: 'index', label: '#', render: (_row, idx) => idx + 1 },
  { id: 'msisdn', label: 'MSISDN', render: (row) => row.msisdn },
  { id: 'iccid', label: 'ICCID', render: (row) => row.iccid },
  { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
];

export default function BatchSwapUpload() {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('idle');

  const reset = () => {
    setFile(null);
    setRows([]);
    setValidationErrors([]);
    setParseError('');
    setSubmitError('');
    setProgress(0);
    setPhase('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setParseError('');
    setSubmitError('');
    setValidationErrors([]);
    setRows([]);
    setProgress(0);
    setPhase('idle');

    try {
      const parsed = await parseBindingsFile(selected);
      const errors = [];
      const annotated = parsed.map((row, idx) => {
        const rowErrors = validateBindingRow(row);
        if (rowErrors.length > 0) {
          errors.push({ line: idx + 2, ...row, errors: rowErrors });
        }
        return { ...row, status: '' };
      });
      setRows(annotated);
      setValidationErrors(errors);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file');
    }
  };

  const handleSubmit = async () => {
    if (rows.length === 0 || validationErrors.length > 0) return;
    setSubmitError('');
    setProgress(0);
    setPhase('uploading');
    setRows((current) => current.map((r) => ({ ...r, status: 'In Progress' })));

    try {
      const payload = rows.map(({ msisdn, iccid }) => ({ msisdn, iccid }));
      await postNumberSwapBindings(payload, {
        onUploadProgress: (e) => {
          if (e.total) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
      setProgress(100);
      setPhase('completed');
      setRows((current) => current.map((r) => ({ ...r, status: 'Success' })));
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Request failed';
      setSubmitError(message);
      setPhase('failed');
      setRows((current) => current.map((r) => ({ ...r, status: 'Failed' })));
    }
  };

  const summary = rows.reduce(
    (acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    },
    { total: rows.length }
  );

  const canSubmit = rows.length > 0 && validationErrors.length === 0 && phase !== 'uploading';

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Batch SIM Swap
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Upload the Jira export. Required columns: <strong>Summary</strong> (9-digit MSISDN) and{' '}
          <strong>Custom field (ICCID/SC serial number)</strong> (20-digit ICCID).
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={phase === 'uploading'}
          >
            {file ? 'Replace file' : 'Choose Excel file'}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Typography variant="body2">
              {file.name} — {rows.length} row{rows.length === 1 ? '' : 's'}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            Execute batch
          </Button>
          <Button variant="text" onClick={reset} disabled={phase === 'uploading'}>
            Reset
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadResultsExcel(rows)}
            disabled={phase !== 'completed' && phase !== 'failed'}
          >
            Download report
          </Button>
        </Stack>

        {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}
        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {validationErrors.length} row(s) have invalid format. Fix the file and re-upload before executing.
            <ul style={{ margin: '8px 0 0 16px' }}>
              {validationErrors.slice(0, 5).map((e) => (
                <li key={e.line}>
                  Row {e.line}: {e.errors.join(', ')} (msisdn="{e.msisdn}", iccid="{e.iccid}")
                </li>
              ))}
              {validationErrors.length > 5 && <li>…and {validationErrors.length - 5} more</li>}
            </ul>
          </Alert>
        )}

        {phase === 'uploading' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Uploading… {progress}%</Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {rows.length > 0 && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <Chip label={`Total: ${summary.total}`} />
              {STATUSES.map(
                (s) =>
                  summary[s.key] > 0 && (
                    <Chip key={s.key} color={s.color} label={`${s.label}: ${summary[s.key]}`} />
                  )
              )}
            </Stack>
            <Scrollbar>
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.id}>{col.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={`${row.msisdn}-${row.iccid}-${idx}`}>
                        {COLUMNS.map((col) => (
                          <TableCell key={col.id}>{col.render(row, idx)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </>
        )}
      </CardContent>
    </Card>
  );
}
